import { Link } from 'react-router-dom';
import type { EntityData } from '@ContractBuilder/types';
import StatusBadge from '@pages/User/components/StatusBadge';
import type { RootResourceStatus, Tag, Team, UISelectionType } from '@root/@types/types';
import { getFormattedStatus, isNonNullish } from '@root/helpers';
import { type CellContext, createColumnHelper } from '@tanstack/react-table';

import {
  classOfBusinessAccessor,
  createdAtAccessor,
  createdByAccessor,
  lastUpdatedByAccessor,
  tagsAccessor,
  teamsAccessor,
  updatedAtAccessor,
} from './shared';
import { brandingAccessor } from './submissions';
import { getBaseAccessor } from './templates';

type Template = EntityData & { created_by: string; last_updated_by?: string; has_default_content?: boolean };

const columnHelper = createColumnHelper<Template>();

export const getTemplatesTableColumns = ({
  brandingFieldName,
  brandingOptions,
  tags,
  templateStatuses,
  teams,
}: {
  brandingFieldName: 'branding' | 'branding_id';
  brandingOptions: UISelectionType[] | string[];
  tags: Tag[];
  templateStatuses: string[];
  teams: Team[];
}) =>
  [
    columnHelper.accessor('id', {
      id: 'id',
      enableColumnFilter: false,
      enableHiding: false,
    }),
    columnHelper.accessor('ref', {
      id: 'ref',
      enableColumnFilter: false,
      header: 'Template ID',
      filterFn: 'arrIncludesSome',
      meta: {
        filter: {
          placeholder: 'All IDs',
        },
      },
      size: 144,
      maxSize: 144,
      sortingFn: 'text',
    }),
    columnHelper.accessor('name', {
      id: 'name',
      enableColumnFilter: false,
      header: 'Template Name',
      sortingFn: 'text',
      sortUndefined: -1,
      filterFn: 'arrIncludesSome',
      enableHiding: false,
      size: 200,
      meta: {
        filter: {
          label: 'Template name',
          placeholder: 'All templates',
        },
        headingCellClassName: 'truncate',
        tooltip: true,
      },
      cell: (cellContext: CellContext<Template, string>) => <span className="truncate">{cellContext.getValue()}</span>,
    }),
    getBaseAccessor<Template>(columnHelper),
    teamsAccessor<Template>(columnHelper, teams),
    columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      filterFn: 'arrIncludesSome',
      size: 148,
      cell: (cellContext: CellContext<Template, RootResourceStatus>) => (
        <StatusBadge className="mt-0" status={cellContext.getValue()} />
      ),
      meta: {
        filter: {
          placeholder: 'All statuses',
          selected: templateStatuses.filter((status) => status !== 'ARCHIVED'),
          options: templateStatuses.map((status) => ({
            name: getFormattedStatus(status),
            value: status,
          })),
        },
        dataCellClassName: '!p-0',
        headingLinkClassName: '!pl-0',
      },
    }),
    columnHelper.accessor((row) => (row.has_default_content ? 'yes' : 'no'), {
      id: 'has_default_content',
      header: 'Content',
      filterFn: 'arrIncludesSome',
      size: 140,
      cell: (cellContext: CellContext<Template, string>) => (
        <div className="inline-flex items-center">
          <span
            data-testid="cell:has_default_content"
            className="inline-flex items-center gap-x-1.5 rounded-xl bg-white px-3 py-1 text-xs font-medium text-gray-900 ring-1 ring-gray-200 ring-inset"
          >
            {cellContext.getValue() === 'yes' ? 'Populated' : 'None'}
          </span>
        </div>
      ),
      meta: {
        filter: {
          label: 'Contains Default Content?',
          placeholder: 'All templates',
          options: [
            { name: 'Populated', value: 'yes' },
            { name: 'None', value: 'no' },
          ],
        },
      },
    }),
    tagsAccessor<Template>(columnHelper, tags),
    lastUpdatedByAccessor<Template>(columnHelper),
    updatedAtAccessor(columnHelper),
    createdByAccessor(columnHelper),
    createdAtAccessor(columnHelper),
    brandingAccessor<Template>(columnHelper, brandingFieldName, brandingOptions),
    classOfBusinessAccessor<Template>(columnHelper),
    columnHelper.display({
      id: 'view',
      header: '',
      enableColumnFilter: false,
      enableHiding: false,
      size: 65,
      minSize: 65,
      maxSize: 65,
      cell: (cellContext: CellContext<Template, unknown>) => (
        <Link
          className="text-primary-600 hover:text-primary-900 font-light"
          target="_self"
          to={`/templates/${cellContext.row.getValue('id')}/contract`}
          rel="noreferrer"
        >
          View
        </Link>
      ),
      meta: {
        dataCellClassName: 'relative truncate whitespace-nowrap px-1 py-0 text-center text-sm font-medium',
      },
    }),
  ].filter(isNonNullish);
